import React from 'react';
import styled from 'styled-components';
import { InputCheckbox, Text } from '@companycam/slab-web';

import type { SelectableItem, UseMultiselectReturn } from './useMultiselect';
import type { MultiselectPickerItemComponentProps } from './types';
import { ItemOverlay } from './ItemOverlay';

type SectionProps<T extends SelectableItem> = {
  selectableItems: T[];
  multiselect: UseMultiselectReturn;
  title: string;
  keyExtractor: (item: T) => string | number;
  ItemComponent: React.ComponentType<MultiselectPickerItemComponentProps<T>>;
  onItemOverlayClicked?: (item: T) => void;
};

const SectionHeader = styled.div`
  padding-bottom: var(--cc_size_spacing_xs);
  display: flex;
  justify-self: flex-start;
`;

const SectionBody = styled.div`
  padding: var(--cc_size_spacing_m) 0 var(--cc_size_spacing_xs) 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  column-gap: var(--cc_size_spacing_s);
  row-gap: var(--cc_size_spacing_s);
`;

const CheckboxTextContainer = styled.div`
  position: relative;
  bottom: 1px;
`;

export const Section = <TItem extends SelectableItem>({
  selectableItems,
  multiselect,
  title,
  keyExtractor,
  ItemComponent,
  onItemOverlayClicked,
}: SectionProps<TItem>) => {
  const id = `multiselect-section-${title}`;
  const isChecked = multiselect.checkGroupSelected(selectableItems);
  return (
    <section key={title}>
      <SectionHeader>
        <InputCheckbox
          design="assetFeed"
          id={id}
          aria-checked={isChecked}
          value={id}
          label={
            <CheckboxTextContainer>
              <Text
                as="p"
                size="m"
                weight="500"
                family="heading"
                ccMargin="none none none xs"
                color="subtle"
              >
                {title}
              </Text>
            </CheckboxTextContainer>
          }
          onChange={() => multiselect.toggleGroupSelected(selectableItems)}
          checked={isChecked}
        />
      </SectionHeader>
      <SectionBody>
        {selectableItems.map((item, index) => (
          <ItemOverlay
            key={keyExtractor(item)}
            label={`item ${index + 1}`}
            item={item}
            multiselect={multiselect}
            ItemComponent={ItemComponent}
            onItemOverlayClicked={onItemOverlayClicked}
          />
        ))}
      </SectionBody>
    </section>
  );
};
